/** Inline product edit for the price book: pencil → name / code / description → save (optionally also updates the linked Xero item). */
import React, { useState } from 'react';
import { Check, Loader2, Pencil, X } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { formatApiError } from '@/utils/apiError';

export function ProductNameEdit({ product, onRenamed }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(product.name);
  const [sku, setSku] = useState(product.sku || '');
  const [description, setDescription] = useState(product.description || '');
  const [pushXero, setPushXero] = useState(!!product.xero_item_id);
  const [busy, setBusy] = useState(false);

  const start = () => { setName(product.name); setSku(product.sku || product.xero_code || ''); setDescription(product.description || ''); setPushXero(!!product.xero_item_id); setEditing(true); };
  const save = async () => {
    const clean = name.replace(/\s+/g, ' ').trim();
    if (!clean) { toast.error('Name cannot be blank'); return; }
    const unchanged = clean === product.name && sku.trim() === (product.sku || '') && description.trim() === (product.description || '');
    if (unchanged) { setEditing(false); return; }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/api/xero/linking/rename`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ product_id: product.id, name: clean, sku: sku.trim(), description: description.trim(), push_xero: pushXero && !!product.xero_item_id }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(formatApiError(d) || 'Save failed');
      toast.success(d.xero_renamed ? `"${clean}" saved — in the app and in Xero` : d.xero_error ? `Saved in the app · Xero said: ${d.xero_error}` : `"${clean}" saved`);
      setEditing(false);
      onRenamed?.({ name: d.name ?? clean, sku: d.sku ?? (sku.trim() || null), description: d.description ?? description.trim() });
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (!editing) {
    return (
      <span className="inline-flex items-center gap-1 group/name">
        <span data-testid={`pricing-name-${product.id}`}>{product.name}</span>
        {product.sku && <span className="text-[10px] font-mono text-gray-400" data-testid={`pricing-sku-${product.id}`}>{product.sku}</span>}
        <button type="button" onClick={start} className="p-0.5 text-gray-300 hover:text-gray-900 group-hover/name:text-gray-500 transition-colors" title="Edit name, code and description" aria-label={`Edit ${product.name}`} data-testid={`pricing-rename-${product.id}`}><Pencil className="w-3.5 h-3.5" /></button>
      </span>
    );
  }
  const onKey = (e) => { if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') save(); if (e.key === 'Escape') setEditing(false); };
  return (
    <div className="my-1 w-full max-w-xl rounded-lg border-2 border-gray-900 bg-white p-2.5 shadow-sm space-y-2" data-testid={`pricing-rename-form-${product.id}`} onKeyDown={onKey}>
      <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-2">
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={160} disabled={busy} className="mt-0.5 h-8 w-full rounded-md border border-gray-300 px-2 text-sm font-medium focus:border-gray-900 focus:outline-none" data-testid={`pricing-rename-input-${product.id}`} />
        </label>
        <label className="block">
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Code</span>
          <input value={sku} onChange={(e) => setSku(e.target.value)} maxLength={30} disabled={busy} placeholder="e.g. PB-MESH" className="mt-0.5 h-8 w-full rounded-md border border-gray-300 px-2 text-sm font-mono focus:border-gray-900 focus:outline-none" data-testid={`pricing-rename-sku-${product.id}`} />
        </label>
      </div>
      <label className="block">
        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Description <span className="font-normal normal-case text-gray-400">(shows on Xero invoices)</span></span>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={4000} disabled={busy} className="mt-0.5 w-full rounded-md border border-gray-300 px-2 py-1 text-sm font-normal focus:border-gray-900 focus:outline-none" data-testid={`pricing-rename-description-${product.id}`} />
      </label>
      <div className="flex flex-wrap items-center gap-2">
        {product.xero_item_id && (
          <label className="inline-flex items-center gap-1 text-[11px] text-gray-600 whitespace-nowrap">
            <input type="checkbox" checked={pushXero} onChange={(e) => setPushXero(e.target.checked)} data-testid={`pricing-rename-xero-${product.id}`} /> also update the Xero item
          </label>
        )}
        <span className="flex-1" />
        <button type="button" onClick={() => setEditing(false)} disabled={busy} className="h-8 px-2.5 rounded-md text-xs font-semibold text-gray-600 hover:bg-gray-100 inline-flex items-center gap-1" data-testid={`pricing-rename-cancel-${product.id}`}><X className="w-3.5 h-3.5" /> Cancel</button>
        <button type="button" onClick={save} disabled={busy} className="h-8 px-3 rounded-md text-xs font-bold bg-gray-900 text-white hover:bg-black disabled:opacity-50 inline-flex items-center gap-1" data-testid={`pricing-rename-save-${product.id}`}>{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save</button>
      </div>
    </div>
  );
}
